/**
 * Settings → AI's draft (slice 1j spec §3.1–§3.7): the edits held in this
 * tab, its one Save, and this tab's save followed to where its run settles.
 */
import { useEffect, useState } from "react";
import { ApiError, errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { useRouting } from "../setup/api";
import type { Catalog } from "../setup/onboarding";
import {
  barView,
  editDraft,
  rebaseDraft,
  sameRouting,
  savedOf,
  saveRequest,
  type Draft,
  type FullRouting,
} from "./routing-draft";

type Ai = NonNullable<Me["computer"]>["ai"];

/** How long "Saved." stays (§3.5). */
const SAVED_MS = 5000;

/**
 * This tab's save, from Save to where its run settles (§3.4, §3.5): `sent`
 * until the `202`'s `connecting` shows, `running` while it runs.
 */
type Saving = "sent" | "running" | null;

function useSaveRun(ai: Ai) {
  const [saving, setSaving] = useState<Saving>(null);
  const [saved, setSaved] = useState(false);
  const connecting = ai.status === "connecting";
  // Followed as `/api/me` moves, in render: each step happens once.
  if (saving === "sent" && connecting) setSaving("running");
  if (saving === "running" && !connecting) {
    setSaving(null);
    // A rolled-back change keeps the card's "The last change didn't apply" instead.
    setSaved(ai.status === "connected" && !ai.error);
  }
  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), SAVED_MS);
    return () => clearTimeout(timer);
  }, [saved]);
  return { saving, setSaving, saved, setSaved };
}

/**
 * The draft (§3.1): every edit lands here, checked as the server would; a
 * routing that changes underneath rebases it (§3.7); Save sends it whole.
 */
export function useRoutingDraft(ai: Ai, catalog: Catalog | undefined) {
  const mutation = useRouting();
  const [open, setOpen] = useState<Draft | null>(null);
  const [refused, setRefused] = useState<{
    hint: string;
    draft: FullRouting;
    retry: boolean;
  } | null>(null);
  const run = useSaveRun(ai);
  // The routing moved underneath: follow it now, before this render shows a stale base.
  if (open && !sameRouting(open.base, savedOf(ai))) setOpen(rebaseDraft(open, ai, catalog));
  const edit = (change: (routing: FullRouting) => FullRouting) => {
    run.setSaved(false);
    setOpen(editDraft(open, ai, change));
  };
  const discard = () => {
    setOpen(null);
    setRefused(null);
  };
  const save = (done?: { onSaved?: () => void; onFailed?: () => void }) => {
    if (!open) return;
    const draft = open.draft;
    mutation.mutate(saveRequest(draft), {
      onSuccess: () => {
        setOpen(null);
        setRefused(null);
        run.setSaving("sent");
        done?.onSaved?.();
      },
      onError: (error) => {
        // `409 ai_busy`: the refetch shows the run, and the bar says so.
        // Only a `400` is this draft's fault; anything else may work again as it is.
        if (!(error instanceof ApiError && error.status === 409)) {
          const retry = !(error instanceof ApiError && error.status === 400);
          setRefused({ hint: errorHint(error), draft, retry });
        }
        done?.onFailed?.();
      },
    });
  };
  const saving = mutation.isPending || run.saving !== null;
  const view = barView({ open, ai, catalog, saving, saved: run.saved, refused });
  return {
    routing: open?.draft ?? savedOf(ai),
    dirty: open !== null,
    edit,
    discard,
    save,
    saving,
    view,
  };
}

export type RoutingDraft = ReturnType<typeof useRoutingDraft>;
